import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import SettingsSidebar from './SettingsSidebar';
import GeneralSettings from './GeneralSettings';
import Passwords from './Passwords';
import UserActivitySettings from './UserActivitySettings';
import PreferencesSettings from './PreferencesSettings';
import CompaniesSettings from './CompaniesSettings';
import LeavePlatform from './LeavePlatform';
import './AccountSettings.css';

const AccountSettings = ({ onClose, onHomeClick, onCategoriesClick, onHowItWorksClick, onProfileClick, onAccountSettingsClick, onForumClick }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('activity');

  const isCompanyMode = Boolean(user?.parent_user_id);
  const hiddenSectionsInCompanyMode = ['passwords', 'companies', 'leave-platform'];

  // Map URL-friendly tab names to internal section IDs
  const tabMapping = {
    'email': 'general',
    'activity': 'activity',
    'passwords': 'passwords',
    'preferences': 'preferences',
    'companies': 'companies',
    'leave-platform': 'leave-platform',
    'leave': 'leave-platform'
  };

  // Reverse mapping: internal section IDs to URL-friendly tab names
  const reverseTabMapping = {
    'general': 'email',
    'activity': 'activity',
    'passwords': 'passwords',
    'preferences': 'preferences',
    'companies': 'companies',
    'leave-platform': 'leave-platform'
  };

  // Read tab parameter from URL on mount
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      const internalSection = tabMapping[tab.toLowerCase()];
      if (internalSection) {
        setActiveSection(internalSection);
      }
    }
  }, [searchParams]);

  // In company mode, if current section is hidden, switch to activity
  useEffect(() => {
    if (isCompanyMode && hiddenSectionsInCompanyMode.includes(activeSection)) {
      setActiveSection('activity');
      navigate('/settings?tab=activity', { replace: true });
    }
  }, [isCompanyMode, activeSection]);

  // Handle section change and update URL
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    // Update URL with user-friendly tab name
    const urlTabName = reverseTabMapping[sectionId] || sectionId;
    navigate(`/settings?tab=${urlTabName}`, { replace: true });
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleMessagesClick = () => {
    // Placeholder
  };

  const handleNotificationsClick = () => {
    // Handled by NotificationSystem component
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'general':
        return <GeneralSettings />;
      case 'passwords':
        return <Passwords />;
      case 'notifications':
        return (
          <div className="settings-placeholder">
            <h2>Notification Settings</h2>
            <p>Notification preferences coming soon...</p>
          </div>
        );
      case 'activity':
        return <UserActivitySettings />;
      case 'preferences':
        return <PreferencesSettings />;
      case 'companies':
        return <CompaniesSettings />;
      case 'leave-platform':
        return <LeavePlatform />;
      default:
        return <GeneralSettings />;
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="account-settings-page account-settings-page-with-bottom-nav">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="account-settings-page-header">
        <Header 
          onLoginClick={() => {}}
          onSignupClick={() => {}}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick || onClose}
          onForumClick={onForumClick}
          currentPage="settings"
        />
      </div>
      
      <div className="account-settings-container">
        <div className="account-settings-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Settings</h1>
              <p>Manage your account security and preferences</p>
            </div>
          </div>
        </div>

        <div className="account-settings-content">
          <div className="settings-layout">
            <SettingsSidebar 
              activeSection={activeSection} 
              onSectionChange={handleSectionChange}
              isCompanyMode={isCompanyMode}
            />
            <div className="settings-main">
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />

      {/* Bottom Navigation Bar */}
      <BottomNavigationBar
        onHomeClick={onHomeClick}
        onLoginClick={() => {}}
        onSignupClick={() => {}}
        onProfileClick={onProfileClick}
        onAccountSettingsClick={onAccountSettingsClick || onClose}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onNotificationsClick={handleNotificationsClick}
        onForumClick={onForumClick}
      />
    </div>
  );
};

export default AccountSettings;
